/**
 * Maps JSON field permission names to actual form field identifiers
 * This allows the permission system to connect to real UI components
 */

export interface FieldMapping {
  jsonSection: string;
  jsonField: string;
  formSection: string;
  formField: string;
  component?: string;
  description?: string;
}

/**
 * Comprehensive field mapping between JSON permissions and form fields
 */
export const FIELD_MAPPINGS: FieldMapping[] = [
  // Feedback Section - UOM Selector
  {
    jsonSection: 'Feedback',
    jsonField: 'Select UOM',
    formSection: 'Feedback',
    formField: 'uom',
    component: 'UOMSelector',
    description: 'Unit of measurement selector',
  },

  // Opportunity Information Section
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Select UOM',
    formSection: 'Opportunity Information',
    formField: 'uom',
    component: 'UOMSelector',
    description: 'Unit of measurement selector',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Region',
    formSection: 'Opportunity Information',
    formField: 'region',
    component: 'RegionSelector',
    description: 'Region selection dropdown',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Requestor Email',
    formSection: 'Opportunity Information',
    formField: 'requestorEmail',
    component: 'FormInput',
    description: 'Requestor email address field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Requestor Role',
    formSection: 'Opportunity Information',
    formField: 'requestorRole',
    component: 'FormInput',
    description: 'Requestor role field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Requestor Name',
    formSection: 'Opportunity Information',
    formField: 'requestorName',
    component: 'FormInput',
    description: 'Requestor name field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Requestor Phone',
    formSection: 'Opportunity Information',
    formField: 'requestorPhone',
    component: 'FormInput',
    description: 'Requestor phone number field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Customer Name',
    formSection: 'Opportunity Information',
    formField: 'customerName',
    component: 'FormInput',
    description: 'Customer name field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Partner Name',
    formSection: 'Opportunity Information',
    formField: 'partnerName',
    component: 'FormInput',
    description: 'Partner name field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Vertical',
    formSection: 'Opportunity Information',
    formField: 'oppVertical',
    component: 'FormDropdown',
    description: 'Customer industry. Other (specify) stores the typed text.',
  },
  {
    jsonSection: 'Opportunity Information',
    // Seeded lookup key. The visible label is "Use case". A missing key is hidden.
    jsonField: 'Usecase',
    formSection: 'Opportunity Information',
    formField: 'oppUsecase',
    component: 'FormDropdown',
    description:
      'What the opportunity is for. Other (specify) stores the typed text.',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Project Name',
    formSection: 'Opportunity Information',
    formField: 'oppProjectName',
    component: 'FormInput',
    description: 'Short project title',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Project Type',
    formSection: 'Opportunity Information',
    formField: 'oppProjectType',
    component: 'FormDropdown',
    description: 'Project type selection',
  },
  {
    jsonSection: 'Opportunity Information',
    // NOTE: `jsonField` is the fieldPermissions.*.json lookup key, NOT a display
    // label. release/1.10 renamed this to match the UI question text and renamed it
    // in all five matrices to suit. That rename is deliberately NOT ported: it is
    // cosmetic, and re-keying the five matrices that #997 just retuned risks
    // silently breaking permission lookups if any one file drifts.
    jsonField: 'AI or Traditional Compute',
    formSection: 'Opportunity Information',
    formField: 'oppWorkloadType',
    component: 'FormDropdown',
    description:
      'Multi-select workload type (Smart IT NVL72 / NVL8 / AI / Traditional)',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Smart IT NVL72 configuration',
    formSection: 'Opportunity Information',
    formField: 'smartItNvl72Config',
    component: 'FormDropdown',
    description: 'Rack configuration for the NVL72 workload pick',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Smart IT NVL8 configuration',
    formSection: 'Opportunity Information',
    formField: 'smartItNvl8Config',
    component: 'FormDropdown',
    description: 'Rack configuration for the NVL8 workload pick',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Project Scope',
    formSection: 'Opportunity Information',
    formField: 'oppProjectScope',
    component: 'FormDropdown',
    description: 'Multi-select project scope',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Deployment Timeline',
    formSection: 'Opportunity Information',
    formField: 'oppDeploymentTimeline',
    component: 'FormInput',
    description: 'Project deployment timeline',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Approximate kW',
    formSection: 'Opportunity Information',
    formField: 'oppApproximateKw',
    component: 'FormInput',
    description: 'Approximate IT load',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Budget',
    formSection: 'Opportunity Information',
    formField: 'oppBudget',
    component: 'FormInput',
    description: 'Budget free text field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Quote Number',
    formSection: 'Opportunity Information',
    formField: 'oppQuoteNumber',
    component: 'FormInput',
    description: 'Quote number field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Team Notes',
    formSection: 'Opportunity Information',
    formField: 'oppTeamNotes',
    component: 'FormInput',
    description: 'Internal team notes',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Deal Registration Number',
    formSection: 'Opportunity Information',
    formField: 'dealRegistrationNumber',
    component: 'FormInput',
    description: 'Deal registration number field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Opportunity Number',
    formSection: 'Opportunity Information',
    formField: 'oppOpportunityId',
    component: 'FormInput',
    description: 'Opportunity number field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Description',
    formSection: 'Opportunity Information',
    formField: 'oppProjectDescription',
    component: 'FormInput',
    description: 'Project description field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Integrated Solution',
    formSection: 'Opportunity Information',
    formField: 'oppIsIntegratedSolution',
    component: 'FormDropdown',
    description: 'Integrated solution dropdown',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Site Address',
    formSection: 'Opportunity Information',
    formField: 'oppSiteAddress',
    component: 'FormInput',
    description: 'Site address field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Reference Design',
    formSection: 'Opportunity Information',
    formField: 'oppRefDesigns',
    component: 'FormInput',
    description:
      'Deprecated: reference design #s are not collected; legacy DB only',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Assessment Date',
    formSection: 'Opportunity Information',
    formField: 'oppPreferredAssessmentDate',
    component: 'FormInput',
    description: 'Assessment date field',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Site Assessment Required',
    formSection: 'Opportunity Information',
    formField: 'oppSiteAssessmentRequired',
    component: 'FormYesNoChoice',
    description: 'Whether a site walk or assessment is required',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Additional Information',
    formSection: 'Opportunity Information',
    formField: 'siteAdditionalSiteInformation',
    component: 'FormInput',
    description: 'Additional Opportunity Information notes',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Site Walk Notes',
    formSection: 'Opportunity Information',
    formField: 'oppSiteWalkNotes',
    component: 'FormInput',
    description: 'Site Walk and facility notes',
  },
  // Hidden from UI, PDF, and Summary — permission mapping retained for DB/AI pipeline
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'BOM Internal Routing',
    formSection: 'Opportunity Information',
    formField: 'bomInternalRouting',
    component: 'FormDropdown',
    description: 'BOM internal routing selection',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Request for Information',
    formSection: 'Opportunity Information',
    formField: 'requestForInformation',
    component: 'FormYesNoChoice',
    description: 'RFI flag',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Config Team Instructions',
    formSection: 'Opportunity Information',
    formField: 'configTeamInstructions',
    component: 'FormInput',
    description: 'Internal instructions for the configuration team',
  },

  // Site Dimensions Section
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Select UOM',
    formSection: 'Site Dimensions',
    formField: 'uom',
    component: 'UOMSelector',
    description:
      'Site dimensions UOM (global selector; same storage as other steps)',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Ceiling Height',
    formSection: 'Site Dimensions',
    formField: 'siteCeilingHeight',
    component: 'FormInput',
    description: 'Ceiling height field',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Room Length',
    formSection: 'Site Dimensions',
    formField: 'siteRoomLengthFtIn',
    component: 'FormInput',
    description: 'Room length field',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Room Width',
    formSection: 'Site Dimensions',
    formField: 'siteRoomWidthSqftSqm',
    component: 'FormInput',
    description: 'Room width field',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Flooring Type',
    formSection: 'Site Dimensions',
    formField: 'siteDataCenterFlooring',
    component: 'FormTwoOptionChoice',
    description: 'Data center flooring field',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Additional Information',
    formSection: 'Site Dimensions',
    formField: 'siteAdditionalSizeInformation',
    component: 'FormInput',
    description: 'Additional size information field',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Floor Weight Support',
    formSection: 'Site Dimensions',
    formField: 'siteWeightSupportOfFloor',
    component: 'FormInput',
    description: 'Weight support of floor (lbs/sqft or kg/m²)',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Height of Raised Floor',
    formSection: 'Site Dimensions',
    formField: 'siteRaisedFloorHeight',
    component: 'FormInput',
    description: 'Height of raised floor (floor structure)',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Dropped Ceiling Height',
    formSection: 'Site Dimensions',
    formField: 'siteDroppedCeilingHeight',
    component: 'FormInput',
    description: 'Dropped ceiling height',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Plenum Height',
    formSection: 'Site Dimensions',
    formField: 'sitePlenumHeight',
    component: 'FormInput',
    description: 'Plenum height if dropped ceiling (space above tile)',
  },
  {
    jsonSection: 'Site Dimensions',
    jsonField: 'Slab to Dropped Ceiling Height',
    formSection: 'Site Dimensions',
    formField: 'siteSlabToDroppedCeilingHeight',
    component: 'FormInput',
    description: 'Slab to dropped ceiling',
  },

  // Facility Power Readiness Section
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Select UOM',
    formSection: 'Facility Power Readiness',
    formField: 'uom',
    component: 'UOMSelector',
    description:
      'Facility power step UOM (global selector; same storage as other steps)',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Description',
    formSection: 'Facility Power Readiness',
    formField: 'powerFacilityDescription',
    component: 'FormInput',
    description: 'Facility power description field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Main nominal Amps',
    formSection: 'Facility Power Readiness',
    formField: 'powerMainNominalAmps',
    component: 'NumberInput',
    description: 'Main nominal amps field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Large UPS Description',
    formSection: 'Facility Power Readiness',
    formField: 'powerLargeUpsDescription',
    component: 'FormInput',
    description: 'Large UPS description field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Fire Suppression Needs',
    formSection: 'Facility Power Readiness',
    formField: 'powerFireSuppressionNeeds',
    component: 'FormInput',
    description: 'Fire suppression needs field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Location',
    formSection: 'Facility Power Readiness',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for power distribution grouping (#999)',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'UPS Output Voltage',
    formSection: 'Facility Power Readiness',
    formField: 'powerUpsOutputVoltage',
    component: 'FormInput',
    description: 'UPS output voltage field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Tap Off Box Requirements',
    formSection: 'Facility Power Readiness',
    formField: 'powerTapOfBoxRequirements',
    component: 'FormInput',
    description: 'Tap off box requirements field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Battery Chemistry',
    formSection: 'Facility Power Readiness',
    formField: 'powerBatteryChemistry',
    component: 'FormDropdown',
    description: 'Battery chemistry field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Additional Power Information',
    formSection: 'Facility Power Readiness',
    formField: 'powerAdditionalInformation',
    component: 'FormInput',
    description: 'Additional power information field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Busway Amperage',
    formSection: 'Facility Power Readiness',
    formField: 'powerBuswayAmperage',
    component: 'FormInput',
    description: 'Busway amperage field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Busway Length',
    formSection: 'Facility Power Readiness',
    formField: 'powerBuswayLengthFt',
    component: 'FormInput',
    description: 'Busway length field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Final MW Size',
    formSection: 'Facility Power Readiness',
    formField: 'powerAnticipatedFinalMwSize',
    component: 'FormInput',
    description: 'Anticipated IT Load MW field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Available Power',
    formSection: 'Facility Power Readiness',
    formField: 'powerTotalAvailablePowerKw',
    component: 'FormInput',
    description: 'Total available power field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Power Inputs per Rack',
    formSection: 'Facility Power Readiness',
    formField: 'powerNumberAndTypeInputPerRack',
    component: 'FormInput',
    description: 'Number and type of power inputs per rack field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Busway or Under Floor',
    formSection: 'Facility Power Readiness',
    formField: 'powerOverheadBuswayOrUnderFloor',
    component: 'FormDropdown',
    description: 'Overhead busway or under floor cabling field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Large UPS Installed',
    formSection: 'Facility Power Readiness',
    formField: 'powerExistingSiteProtectedByLargeKwUps',
    component: 'FormDropdown',
    description: 'Existing site protected by UPS field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Existing UPS Model',
    formSection: 'Facility Power Readiness',
    formField: 'powerModelOfExistingUps',
    component: 'FormInput',
    description: 'Model of existing UPS field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Redundancy Level',
    formSection: 'Facility Power Readiness',
    formField: 'powerLevelOfRedundancy',
    component: 'FormDropdown',
    description: 'Level of redundancy field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'UPS Redundancy Required',
    formSection: 'Facility Power Readiness',
    formField: 'powerRequiredRedundancyForUps',
    component: 'FormDropdown',
    description: 'Required redundancy for UPS system field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Power Location',
    formSection: 'Facility Power Readiness',
    formField: 'powerSourceLocation',
    component: 'FormDropdown',
    description: 'Power source location field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Main Voltage',
    formSection: 'Facility Power Readiness',
    formField: 'powerMainNominalLvVoltage',
    component: 'FormInput',
    description: 'Main nominal low voltage field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Total IT Load',
    formSection: 'Facility Power Readiness',
    formField: 'powerTotalDataCenterItLoadKw',
    component: 'FormInput',
    description: 'Total data center IT load field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Average Load per Rack',
    formSection: 'Facility Power Readiness',
    formField: 'powerAverageItLoadPerRackKw',
    component: 'FormInput',
    description: 'Average IT load per rack field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Max Load per Rack',
    formSection: 'Facility Power Readiness',
    formField: 'powerMaximumItLoadPerRackKw',
    component: 'FormInput',
    description: 'Maximum IT load per rack field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Distribution Voltage',
    formSection: 'Facility Power Readiness',
    formField: 'powerDistributionVoltageToLoad',
    component: 'FormInput',
    description: 'Distribution voltage to load field',
  },
  {
    jsonSection: 'Facility Power Readiness',
    jsonField: 'Battery Runtime',
    formSection: 'Facility Power Readiness',
    formField: 'powerBatteryRuntimeRequiredMin',
    component: 'FormInput',
    description: 'Battery runtime required field',
  },

  // Rack Power Section
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Make/Model',
    formSection: 'Rack Power',
    formField: 'rackPduMakeModel',
    component: 'FormInput',
    description: 'PDU make/model field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Mounting Type',
    formSection: 'Rack Power',
    formField: 'rackPduMountingType',
    component: 'FormDropdown',
    description: 'PDU mounting type field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'RPDUs per Rack',
    formSection: 'Rack Power',
    formField: 'rackPduCountPerRack',
    component: 'FormInput',
    description: 'Number of PDUs per rack field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Outlet Type',
    formSection: 'Rack Power',
    formField: 'rackPduOutletSwitchingMonitored',
    component: 'FormDropdown',
    description: 'Outlet switching/monitored field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Outlet Quantities',
    formSection: 'Rack Power',
    formField: 'rackPduOutletTypesQuantity',
    component: 'FormInput',
    description: 'Outlet types and quantities field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'AC or DC Power',
    formSection: 'Rack Power',
    formField: 'rackPduPowerType',
    component: 'FormInput',
    description: 'PDU power type field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Max kW per Rack',
    formSection: 'Rack Power',
    formField: 'rackPduMaxKwPerRack',
    component: 'FormInput',
    description: 'Maximum kW per rack field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Input',
    formSection: 'Rack Power',
    formField: 'rackPduInput',
    component: 'FormInput',
    description: 'PDU input field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Output',
    formSection: 'Rack Power',
    formField: 'rackPduOutput',
    component: 'FormInput',
    description: 'PDU output field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Additional Information',
    formSection: 'Rack Power',
    formField: 'rackPduAdditionalInfo',
    component: 'FormInput',
    description: 'Additional rack power information field',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Manufacturer',
    formSection: 'Rack Power',
    formField: 'rackPduManufacturer',
    component: 'FormInput',
    description: 'PDU manufacturer',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Model',
    formSection: 'Rack Power',
    formField: 'rackPduModel',
    component: 'FormInput',
    description: 'PDU model',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Input Plug Type',
    formSection: 'Rack Power',
    formField: 'rackPduInputPlugType',
    component: 'FormInput',
    description: 'PDU input plug type',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'DC Power Shelves',
    formSection: 'Rack Power',
    formField: 'rackPduDcPowerShelves',
    component: 'NumberInput',
    description: 'DC power shelves count',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Capacity Amps',
    formSection: 'Rack Power',
    formField: 'rackPduCapacityAmps',
    component: 'NumberInput',
    description: 'PDU capacity amps',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Capacity Volts',
    formSection: 'Rack Power',
    formField: 'rackPduCapacityVolts',
    component: 'NumberInput',
    description: 'PDU capacity volts',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Capacity kW',
    formSection: 'Rack Power',
    formField: 'rackPduCapacityKw',
    component: 'NumberInput',
    description: 'PDU capacity kW',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'Load Readings kW',
    formSection: 'Rack Power',
    formField: 'rackPduLoadReadingsKw',
    component: 'FormInput',
    description: 'PDU load readings kW',
  },
  {
    jsonSection: 'Rack Power',
    jsonField: 'PDU Location',
    formSection: 'Rack Power',
    formField: 'rackPduLocation',
    component: 'FormInput',
    description: 'PDU location in rack',
  },

  // Racks Section
  {
    jsonSection: 'Racks',
    jsonField: 'Additional Rack Information',
    formSection: 'Racks',
    formField: 'rackAdditionalRackInformation',
    component: 'FormInput',
    description: 'Additional rack information field',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Additional Information',
    formSection: 'Racks',
    formField: 'rackAdditionalInformation',
    component: 'FormInput',
    description: 'Additional rack information field',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Length',
    formSection: 'Racks',
    formField: 'rackDimensionsLength',
    component: 'FormInput',
    description: 'Rack depth field',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Height',
    formSection: 'Racks',
    formField: 'rackDimensionsHeight',
    component: 'FormInput',
    description: 'Rack height field',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Width',
    formSection: 'Racks',
    formField: 'rackDimensionsWidth',
    component: 'FormInput',
    description: 'Rack width field',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Platform',
    formSection: 'Racks',
    formField: 'rackPlatform',
    component: 'FormDropdown',
    description: 'Rack platform (project details)',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Location',
    formSection: 'Racks',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for rack grouping (#999)',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Description',
    formSection: 'Racks',
    formField: 'rackDescription',
    component: 'FormInput',
    description: 'Rack description (project details)',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Use',
    formSection: 'Racks',
    formField: 'rackUse',
    component: 'FormInput',
    description: 'Rack use (project details)',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Quantity',
    formSection: 'Racks',
    formField: 'rackQuantity',
    component: 'NumberInput',
    description: 'Rack quantity (project details)',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Seismic Rated',
    formSection: 'Racks',
    formField: 'rackSeismicRated',
    component: 'FormYesNoChoice',
    description: 'Seismic rated racks',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Max Load Rating',
    formSection: 'Racks',
    formField: 'rackMaxLoadRating',
    component: 'FormDropdown',
    description: 'Max load rating',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Rack Color',
    formSection: 'Racks',
    formField: 'rackColor',
    component: 'FormDropdown',
    description: 'Rack color',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Liquid to Chip Cooling',
    formSection: 'Racks',
    formField: 'rackRequiresLiquidToChip',
    component: 'FormYesNoChoice',
    description: 'Liquid to chip cooling desired',
  },
  {
    jsonSection: 'Racks',
    jsonField: 'Describe Rack Elevations',
    formSection: 'Racks',
    formField: 'rackDescribeElevations',
    component: 'FormInput',
    description: 'Describe rack elevations',
  },

  // Buttons Section
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Save',
    formSection: 'Buttons',
    formField: 'saveButton',
    component: 'TouchableOpacity',
    description: 'Save button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Edit',
    formSection: 'Buttons',
    formField: 'editButton',
    component: 'TouchableOpacity',
    description: 'Edit button (uses Save permissions)',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Submit',
    formSection: 'Buttons',
    formField: 'submitButton',
    component: 'TouchableOpacity',
    description: 'Submit button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Cancel',
    formSection: 'Buttons',
    formField: 'cancelButton',
    component: 'TouchableOpacity',
    description: 'Cancel button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Previous',
    formSection: 'Buttons',
    formField: 'previousButton',
    component: 'TouchableOpacity',
    description: 'Previous button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Next',
    formSection: 'Buttons',
    formField: 'nextButton',
    component: 'TouchableOpacity',
    description: 'Next button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - Delete',
    formSection: 'Buttons',
    formField: 'deleteButton',
    component: 'TouchableOpacity',
    description: 'Delete button',
  },
  {
    jsonSection: 'Buttons',
    jsonField: 'Button - New Request Split',
    formSection: 'Buttons',
    formField: 'newRequestSplitButton',
    component: 'NewRequestSplitButton',
    description:
      'Split button for New Request (default) and Import from File (dropdown)',
  },

  // Supporting Documents Section
  {
    jsonSection: 'Supporting Documentation',
    jsonField: 'Supporting Documents',
    formSection: 'Supporting Documentation',
    formField: 'supportingDocuments',
    component: 'FileUpload',
    description: 'Supporting documents upload',
  },

  // Liquid Cooling Section
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Make/Model',
    formSection: 'Liquid Cooling',
    formField: 'lcCduMakeModel',
    component: 'FormInput',
    description: 'CDU make and model field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Rating',
    formSection: 'Liquid Cooling',
    formField: 'lcCduRating',
    component: 'FormInput',
    description: 'CDU rating field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Configuration',
    formSection: 'Liquid Cooling',
    formField: 'lcCduConfiguration',
    component: 'FormInput',
    description: 'CDU configuration field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Select UOM',
    formSection: 'Liquid Cooling',
    formField: 'lcUom',
    component: 'UOMSelector',
    description: 'Liquid cooling UOM selector field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Redundancy Strategy',
    formSection: 'Liquid Cooling',
    formField: 'lcRedundancyStrategy',
    component: 'FormInput',
    description: 'Redundancy strategy field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Coolant',
    formSection: 'Liquid Cooling',
    formField: 'lcCoolant',
    component: 'FormDropdown',
    description: 'Coolant selection field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Rack Pressure Drop',
    formSection: 'Liquid Cooling',
    formField: 'lcRackPressureDrop',
    component: 'FormInput',
    description: 'Rack pressure drop field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Additional Information SFN',
    formSection: 'Liquid Cooling',
    formField: 'lcAdditionalInformationSfn',
    component: 'FormInput',
    description: 'Additional information SFN field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Coolant',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsCoolant',
    component: 'FormInput',
    description: 'TCS coolant field (auto-filled)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Additional Information TCS',
    formSection: 'Liquid Cooling',
    formField: 'lcAdditionalInformationTcs',
    component: 'FormInput',
    description: 'Additional information TCS field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Total IT Load',
    formSection: 'Liquid Cooling',
    formField: 'lcTotalItLoadPerDataHall',
    component: 'FormInput',
    description: 'Total IT load per datahall field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Ceiling Support Rating',
    formSection: 'Liquid Cooling',
    formField: 'lcDatahallCeilingStructuralSupportRating',
    component: 'FormInput',
    description: 'Ceiling structural support rating field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Redundancy',
    formSection: 'Liquid Cooling',
    formField: 'lcCduRedundancyDefinition',
    component: 'FormInput',
    description: 'CDU redundancy definition field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Additional Information',
    formSection: 'Liquid Cooling',
    formField: 'lcAdditionalInformation',
    component: 'FormInput',
    description: 'Additional liquid cooling information field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Thermal Load per SFN',
    formSection: 'Liquid Cooling',
    formField: 'lcTotalLiquidCoolingThermalLoadPerSfn',
    component: 'FormInput',
    description: 'Total liquid cooling thermal load per SFN field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Liquid Cooled Racks',
    formSection: 'Liquid Cooling',
    formField: 'lcNumberOfLiquidCooledRacksToBeServed',
    component: 'FormInput',
    description: 'Number of liquid cooled racks to be served field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Air Cooled Racks',
    formSection: 'Liquid Cooling',
    formField: 'lcNumberOfAirCooledRacks',
    component: 'FormInput',
    description: 'Number of air cooled racks field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Liquid Cooling Percentage',
    formSection: 'Liquid Cooling',
    formField: 'lcLiquidCoolingVsPeakRackItLoad',
    component: 'FormInput',
    description: 'Liquid cooling vs peak rack IT load percentage field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Glycol Percentage',
    formSection: 'Liquid Cooling',
    formField: 'lcEthyleneGlycolPct',
    component: 'FormInput',
    description: 'Ethylene glycol percentage field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Primary Supply Temp',
    formSection: 'Liquid Cooling',
    formField: 'lcTps',
    component: 'FormInput',
    description: 'Primary supply temperature field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Primary Return Temp',
    formSection: 'Liquid Cooling',
    formField: 'lcTpr',
    component: 'FormInput',
    description: 'Primary return temperature field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Primary Connection Type',
    formSection: 'Liquid Cooling',
    formField: 'lcConnectionTypePrimary',
    component: 'FormDropdown',
    description: 'Connection type primary field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Primary Valve',
    formSection: 'Liquid Cooling',
    formField: 'lcPrimaryFlowValve',
    component: 'FormDropdown',
    description: 'Primary flow valve field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Secondary Supply Temp',
    formSection: 'Liquid Cooling',
    formField: 'lcTss',
    component: 'FormInput',
    description: 'Secondary supply temperature field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Secondary Return Temp',
    formSection: 'Liquid Cooling',
    formField: 'lcTsr',
    component: 'FormInput',
    description: 'Secondary return temperature field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Flowrate Required',
    formSection: 'Liquid Cooling',
    formField: 'lcFlowrateRequirement',
    component: 'FormInput',
    description: 'Flowrate requirement field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Filtration',
    formSection: 'Liquid Cooling',
    formField: 'lcFiltration',
    component: 'FormDropdown',
    description: 'Filtration micron level field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Max Pressure',
    formSection: 'Liquid Cooling',
    formField: 'lcMaximumPressureRating',
    component: 'FormInput',
    description: 'Maximum pressure rating field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'External Pressure Required',
    formSection: 'Liquid Cooling',
    formField: 'lcExternalStaticPressureRequirement',
    component: 'FormInput',
    description: 'External static pressure requirement field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Secondary Connection Type',
    formSection: 'Liquid Cooling',
    formField: 'lcConnectionTypeSecondary',
    component: 'FormDropdown',
    description: 'Connection type secondary field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Secondary Network Required',
    formSection: 'Liquid Cooling',
    formField: 'lcSecondaryFluidNetwork',
    component: 'FormDropdown',
    description: 'Secondary fluid network required field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Floor Type',
    formSection: 'Liquid Cooling',
    formField: 'lcDatahallFloorType',
    component: 'FormDropdown',
    description: 'Datahall floor type field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Raised Floor Depth',
    formSection: 'Liquid Cooling',
    formField: 'lcDatahallRaisedFloorDepth',
    component: 'FormInput',
    description: 'Datahall raised floor depth field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Rack Arrangement',
    formSection: 'Liquid Cooling',
    formField: 'lcRackArrangementWithinTheDatahall',
    component: 'FormInput',
    description: 'Rack arrangement within datahall field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Arrangement',
    formSection: 'Liquid Cooling',
    formField: 'lcCduArrangementWithinTheDatahall',
    component: 'FormDropdown',
    description: 'CDU arrangement within datahall field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Primary Piping',
    formSection: 'Liquid Cooling',
    formField: 'lcCduPrimaryPipingConnections',
    component: 'FormDropdown',
    description: 'CDU primary piping connections field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Secondary Piping',
    formSection: 'Liquid Cooling',
    formField: 'lcCduSecondaryPipingConnections',
    component: 'FormDropdown',
    description: 'CDU secondary piping connections field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Flow Pathway',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsFlowPathwayDefinition',
    component: 'FormInput',
    description: 'TCS flow pathway definition field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Arrangement',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsArrangementType',
    component: 'FormDropdown',
    description: 'TCS arrangement type field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Location',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsRoutingLocation',
    component: 'FormDropdown',
    description: 'TCS routing location field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Special Requirements',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsRoutingSpecialRequirements',
    component: 'FormInput',
    description: 'TCS routing special requirements field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Control Type',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsRackControlType',
    component: 'FormDropdown',
    description: 'TCS rack control type field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Connection Size',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsRackConnectionSize',
    component: 'FormDropdown',
    description: 'TCS rack connection size field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Connection Type',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsRackConnectionType',
    component: 'FormDropdown',
    description: 'TCS rack connection type field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Max Pressure',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsLoopPressureMaximumRating',
    component: 'FormInput',
    description: 'TCS loop pressure maximum rating field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Max Temperature',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsLoopTemperatureMaximumRating',
    component: 'FormInput',
    description: 'TCS loop temperature maximum rating field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Pipe Diameter',
    formSection: 'Liquid Cooling',
    formField: 'lcTcsLoopPipeDiameter',
    component: 'FormInput',
    description: 'TCS loop pipe diameter field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Flowrate per Rack',
    formSection: 'Liquid Cooling',
    formField: 'lcRequiredTcsCoolantFlowratePerRack',
    component: 'FormInput',
    description: 'Required TCS coolant flowrate per rack field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Ceiling Height',
    formSection: 'Liquid Cooling',
    formField: 'lcDatahallCeilingHeight',
    component: 'FormInput',
    description: 'Datahall ceiling height field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Liquid Cooling Required',
    formSection: 'Liquid Cooling',
    formField: 'lcProjectRequiresLiquidCooling',
    component: 'FormDropdown',
    description: 'Does this project require liquid cooling field',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: '2D datahall layout showing rack positions',
    formSection: 'Liquid Cooling',
    formField: '2dDatahallLayoutShowingRackPositions',
    component: 'FileUpload',
    description: '2D datahall layout showing rack positions file upload',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Additional Attachments Secondary Loop',
    formSection: 'Liquid Cooling',
    formField: 'additionalAttachmentsSecondaryLoop',
    component: 'FileUpload',
    description: 'Additional attachments for secondary loop file upload',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: '3D Model Datahall',
    formSection: 'Liquid Cooling',
    formField: '3dModelDatahall',
    component: 'FileUpload',
    description: '3D model of datahall with obstructions',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: '2D Layout Datahall',
    formSection: 'Liquid Cooling',
    formField: '2dLayoutDatahall',
    component: 'FileUpload',
    description: '2D layout of datahall with obstructions',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Rack Arrangement Files',
    formSection: 'Liquid Cooling',
    formField: 'rackArrangementFiles',
    component: 'FileUpload',
    description: 'Rack arrangement supporting files',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'TCS Additional Attachments',
    formSection: 'Liquid Cooling',
    formField: 'tcsAdditionalAttachments',
    component: 'FileUpload',
    description: 'TCS additional attachments',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Location',
    formSection: 'Liquid Cooling',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for liquid cooling CDU grouping (#999)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Liquid Cooling Notes',
    formSection: 'Liquid Cooling',
    formField: 'lcDescription',
    component: 'FormInput',
    description: 'Liquid cooling notes / description (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Configuration',
    formSection: 'Liquid Cooling',
    formField: 'lcCduType',
    component: 'FormDropdown',
    description: 'Rack or Row CDU placement (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Liquid to Chip Desired',
    formSection: 'Liquid Cooling',
    formField: 'lcLiquidToChipDesired',
    component: 'FormDropdown',
    description: 'Liquid-to-chip cooling desired (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Vertiv Row Manifolds',
    formSection: 'Liquid Cooling',
    formField: 'lcVertivSuppliesRowManifolds',
    component: 'FormDropdown',
    description: 'Vertiv supplies row manifolds (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Vertiv Rack Manifolds',
    formSection: 'Liquid Cooling',
    formField: 'lcVertivSuppliesRackManifolds',
    component: 'FormDropdown',
    description: 'Vertiv supplies rack manifolds (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'CDU Redundancy',
    formSection: 'Liquid Cooling',
    formField: 'lcCduRedundancy',
    component: 'FormDropdown',
    description: 'CDU redundancy N/N+1/2N (Stream 2)',
  },
  {
    jsonSection: 'Liquid Cooling',
    jsonField: 'Leak Detection Approach',
    formSection: 'Liquid Cooling',
    formField: 'lcLeakDetectionApproach',
    component: 'FormInput',
    description: 'Leak detection control approach (Stream 2)',
  },

  // Team Information (from OpportunityStep)
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Vertiv Sponsors',
    formSection: 'Team Information',
    formField: 'vertivSponsors',
    component: 'TeamMemberSelector',
    description: 'Vertiv sponsors selection',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Vertiv Contributors',
    formSection: 'Team Information',
    formField: 'vertivContributors',
    component: 'TeamMemberSelector',
    description: 'Vertiv contributors selection',
  },
  {
    jsonSection: 'Opportunity Information',
    jsonField: 'Partner Contributors',
    formSection: 'Team Information',
    formField: 'partnerContributors',
    component: 'TeamMemberSelector',
    description: 'Partner contributors selection',
  },

  // Small UPS Section — jsonField names match `fieldPermissions.*.json` ("Small UPS" section).
  {
    jsonSection: 'Small UPS',
    jsonField: 'Location',
    formSection: 'Small UPS',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for Small UPS grouping (#999)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Make/Model',
    formSection: 'Small UPS',
    formField: 'smallUpsManufacturer',
    component: 'FormInput',
    description: 'Manufacturer (same RBAC bucket as legacy make/model)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Make/Model',
    formSection: 'Small UPS',
    formField: 'smallUpsModel',
    component: 'FormInput',
    description: 'Model',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'powerSmallUpsDescription',
    component: 'FormInput',
    description: 'Description (legacy UPS Rating permission bucket)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Make/Model',
    formSection: 'Small UPS',
    formField: 'smallUpsSerialNumber',
    component: 'FormInput',
    description: 'Serial number',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Type',
    formSection: 'Small UPS',
    formField: 'powerTypesOfUps',
    component: 'FormDropdown',
    description: 'Type of UPS',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Input Voltage',
    formSection: 'Small UPS',
    formField: 'smallUpsInputVoltage',
    component: 'FormInput',
    description: 'Input voltage',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Input Voltage',
    formSection: 'Small UPS',
    formField: 'smallUpsInputPlug',
    component: 'FormInput',
    description: 'Input plug (shares Input Voltage permission bucket)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Input Phase',
    formSection: 'Small UPS',
    formField: 'powerInputPowerPhase',
    component: 'FormDropdown',
    description: 'Input power phase',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsCapacity',
    component: 'FormInput',
    description: 'UPS capacity (kVA)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsLoadPercentage',
    component: 'FormInput',
    description: 'Load percentage',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Runtime',
    formSection: 'Small UPS',
    formField: 'powerRuntime',
    component: 'FormInput',
    description: 'Battery runtime',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsBatteryCabinetQty',
    component: 'FormInput',
    description: 'Quantity of battery cabinets',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsMounting',
    component: 'FormDropdown',
    description: 'Mounting',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Mount Type',
    formSection: 'Small UPS',
    formField: 'smallUpsMountType',
    component: 'FormDropdown',
    description: 'Mount Type (Wall, Floor, Rack)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsOutlets',
    component: 'FormInput',
    description: 'Number of outlets',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Maintenance Bypass Required',
    formSection: 'Small UPS',
    formField: 'smallUpsMaintenanceBypassRequired',
    component: 'FormYesNoChoice',
    description: 'Maintenance bypass required',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Expected Load Growth',
    formSection: 'Small UPS',
    formField: 'smallUpsExpectedLoadGrowth',
    component: 'FormInput',
    description: 'Expected load growth (request UOM)',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Will Load Be On Generator?',
    formSection: 'Small UPS',
    formField: 'smallUpsLoadOnGenerator',
    component: 'FormYesNoChoice',
    description: 'Whether the small UPS load will be on generator',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'What Sensors Are Required',
    formSection: 'Small UPS',
    formField: 'smallUpsRequiredSensors',
    component: 'FormInput',
    description: 'What sensors are required',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'UPS Rating',
    formSection: 'Small UPS',
    formField: 'smallUpsBackupGenerator',
    component: 'FormYesNoChoice',
    description: 'Backup generator',
  },
  {
    jsonSection: 'Small UPS',
    jsonField: 'Additional Information',
    formSection: 'Small UPS',
    formField: 'powerSmallUpsAdditionalInfo',
    component: 'FormInput',
    description: 'Notes (additional information)',
  },

  // Large UPS standalone step (#1330) — RBAC separate from Facility Power Readiness inline fields.
  {
    jsonSection: 'Large UPS',
    jsonField: 'Location',
    formSection: 'Large UPS',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for Large UPS grouping (#999)',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Large UPS Description',
    formSection: 'Large UPS',
    formField: 'powerLargeUpsDescription',
    component: 'FormInput',
    description: 'Large UPS unit description',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Large UPS Installed',
    formSection: 'Large UPS',
    formField: 'powerExistingSiteProtectedByLargeKwUps',
    component: 'FormDropdown',
    description: 'Existing site protected by large kW UPS',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Existing UPS Model',
    formSection: 'Large UPS',
    formField: 'powerModelOfExistingUps',
    component: 'FormInput',
    description: 'Model of existing UPS',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Redundancy Level',
    formSection: 'Large UPS',
    formField: 'powerLevelOfRedundancy',
    component: 'FormDropdown',
    description: 'Level of redundancy',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'UPS Redundancy Required',
    formSection: 'Large UPS',
    formField: 'powerRequiredRedundancyForUps',
    component: 'FormDropdown',
    description: 'Required redundancy for UPS',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Battery Chemistry',
    formSection: 'Large UPS',
    formField: 'powerBatteryChemistry',
    component: 'FormDropdown',
    description: 'Battery chemistry',
  },
  {
    jsonSection: 'Large UPS',
    jsonField: 'Battery Runtime',
    formSection: 'Large UPS',
    formField: 'powerBatteryRuntimeRequiredMin',
    component: 'FormInput',
    description: 'Battery runtime required (minutes)',
  },

  // Cooling Readiness Section
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Location',
    formSection: 'Cooling Readiness',
    formField: 'locationTagId',
    component: 'FormDropdown',
    description: 'Location tag for cooling system grouping (#999)',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Description',
    formSection: 'Cooling Readiness',
    formField: 'coolingDescription',
    component: 'FormInput',
    description: 'Cooling description field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Chilled Water Available',
    formSection: 'Cooling Readiness',
    formField: 'coolingIsChilledWaterAvailable',
    component: 'FormDropdown',
    description: 'Chilled water available onsite field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Cooling Redundancy Required',
    formSection: 'Cooling Readiness',
    formField: 'coolingRequiredRedundancyForSystem',
    component: 'FormInput',
    description: 'Required redundancy for cooling system field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Existing Cooling System',
    formSection: 'Cooling Readiness',
    formField: 'coolingIsExistingSystemOnsite',
    component: 'FormDropdown',
    description: 'Existing cooling system onsite field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Available Cooling Capacity',
    formSection: 'Cooling Readiness',
    formField: 'coolingTotalAvailableCapacityKw',
    component: 'FormInput',
    description: 'Total available cooling capacity field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Rack Temperature Rise',
    formSection: 'Cooling Readiness',
    formField: 'coolingTempRisePrefThroughItEquipment',
    component: 'FormInput',
    description: 'Temperature rise preference through IT equipment field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Additional Information',
    formSection: 'Cooling Readiness',
    formField: 'coolingAdditionalReadinessInfo',
    component: 'FormInput',
    description: 'Additional cooling readiness information field',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Describe Cooling Approach',
    formSection: 'Cooling Readiness',
    formField: 'coolingDescribeApproach',
    component: 'FormInput',
    description: 'Describe cooling approach (Stream 2)',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Rear Door HX Desired',
    formSection: 'Cooling Readiness',
    formField: 'coolingRearDoorHexDesired',
    component: 'FormDropdown',
    description: 'Rear door heat exchanger desired (Stream 2)',
  },
  {
    jsonSection: 'Cooling Readiness',
    jsonField: 'Describe Rear Door Liquid Loop',
    formSection: 'Cooling Readiness',
    formField: 'coolingDescribeRearDoorLoop',
    component: 'FormInput',
    description: 'Describe rear door liquid loop (Stream 2)',
  },

  // Monitoring Management Section
  {
    jsonSection: 'Monitoring Management',
    jsonField: 'Monitoring Make/Model',
    formSection: 'Monitoring Management',
    formField: 'mgmtDescribeMonitoringControlSoftwareNeeds',
    component: 'FormInput',
    description: 'Describe Monitoring & Control Software needs field',
  },
  {
    jsonSection: 'Monitoring Management',
    jsonField: 'Additional Information',
    formSection: 'Monitoring Management',
    formField: 'mgmtAdditionalRemoteMgmtInfo',
    component: 'FormInput',
    description: 'Additional remote management information field',
  },

  // Feedback Section
  {
    jsonSection: 'Feedback',
    jsonField: 'Rating',
    formSection: 'Feedback',
    formField: 'fb_rating',
    component: 'StarRating',
    description: 'Feedback rating field',
  },
  {
    jsonSection: 'Feedback',
    jsonField: 'Comments',
    formSection: 'Feedback',
    formField: 'fb_comments',
    component: 'TextInput',
    description: 'Feedback comments field',
  },

  // Deliverables (#1450). The section was added to all five stage matrices and had no entry
  // here, so the seeder resolved every cell to `field_key: null` and the rows seeded
  // unenforceable — the DRAFT matrix hides the section from 11 of 14 roles and every one of
  // them could see it. (MR !489 review, batch 2 change 2.1.)
  //
  // `formField` is not a free choice. It becomes the `field_key`, and both guards match on
  // it by name: the write guard against top-level REQUEST BODY keys, the read guard against
  // RESPONSE properties. The body is `{ base, deliverables }` and the response is
  // `{ deliverables, total }`, so `deliverables` is the only value that works on both sides.
  //
  // One entry, because the matrix has one cell. The list is governed as a whole rather than
  // per row: `itemType` is the identity of a row, not an editable field, and `comment` has no
  // cell of its own.
  {
    jsonSection: 'Deliverables',
    jsonField: 'Deliverables',
    formSection: 'Deliverables',
    formField: 'deliverables',
    component: 'RequestDeliverablesEditor',
    description: 'What the requester wants Vertiv to produce, as a whole list',
  },
];

/**
 * Service for mapping between JSON field permissions and form fields
 */
export class FieldMappingService {
  private mappingCache = new Map<string, FieldMapping>();

  constructor() {
    this.buildCache();
  }

  private buildCache(): void {
    this.mappingCache.clear(); // Clear existing cache
    FIELD_MAPPINGS.forEach(mapping => {
      const key = this.createMappingKey(mapping.jsonSection, mapping.jsonField);
      this.mappingCache.set(key, mapping);
    });
  }

  /**
   * Refresh the mapping cache (useful for development)
   */
  refreshCache(): void {
    this.buildCache();
  }

  private createMappingKey(section: string, field: string): string {
    return `${section}.${field}`;
  }

  /**
   * Find form field mapping from JSON permission field
   */
  getFormFieldMapping(
    jsonSection: string,
    jsonField: string,
  ): FieldMapping | null {
    const key = this.createMappingKey(jsonSection, jsonField);
    return this.mappingCache.get(key) || null;
  }

  /**
   * Find JSON permission field from form field
   */
  getJsonFieldMapping(
    formSection: string,
    formField: string,
  ): FieldMapping | null {
    return (
      FIELD_MAPPINGS.find(
        mapping =>
          mapping.formSection === formSection &&
          mapping.formField === formField,
      ) || null
    );
  }

  /**
   * Resolve RBAC mapping by form field id alone (first match wins).
   * Used by Summary and registry projections — permission keys are not stored on field registry rows.
   */
  getMappingByFormField(formField: string): FieldMapping | null {
    return (
      FIELD_MAPPINGS.find(mapping => mapping.formField === formField) || null
    );
  }

  /**
   * Get all mappings for a JSON section
   */
  getMappingsForJsonSection(jsonSection: string): FieldMapping[] {
    return FIELD_MAPPINGS.filter(
      mapping => mapping.jsonSection === jsonSection,
    );
  }

  /**
   * Get all mappings for a form section
   */
  getMappingsForFormSection(formSection: string): FieldMapping[] {
    return FIELD_MAPPINGS.filter(
      mapping => mapping.formSection === formSection,
    );
  }

  /**
   * Check if a field has a mapping
   */
  hasMapping(jsonSection: string, jsonField: string): boolean {
    return this.getFormFieldMapping(jsonSection, jsonField) !== null;
  }

  /**
   * Get all available JSON sections
   */
  getJsonSections(): string[] {
    const sections = new Set(
      FIELD_MAPPINGS.map(mapping => mapping.jsonSection),
    );
    return Array.from(sections);
  }

  /**
   * Get all available form sections
   */
  getFormSections(): string[] {
    const sections = new Set(
      FIELD_MAPPINGS.map(mapping => mapping.formSection),
    );
    return Array.from(sections);
  }
}

// Export singleton instance
export const fieldMappingService = new FieldMappingService();
